// Implements: F-UIX-010, F-UIX-018
/**
 * The sentence of a usage refusal (UIX.2, phase 7).
 *
 * A refusal is `USAGE_LIMIT_REACHED` (403) with a `reason` and, except for the personal scope,
 * the `renews_at` of the limit. The client writes the sentence from the reason, in the words
 * the backend uses (USG.11): which limit cut, when it renews and whom to contact, never an
 * amount. A reason it does not know is still a usage refusal, so it never reads as a generic
 * failure or as "unavailable". The same reasons close a turn that a race refused in the job
 * (its `error_code`), which is why `isUsageReason` is exported.
 *
 * The renewal date is the calendar date written in the ISO text itself: the server renders it
 * with the offset of the month window's zone, so reading the `YYYY-MM-DD` part gives that
 * zone's date whatever the zone of the browser. A date that cannot be read is left out rather
 * than guessed.
 */

import { PLATFORM_LOCALE } from "../../../types/locale";
import { t } from "../../i18n";

/** The refusal code of a usage limit. */
export const USAGE_LIMIT_REACHED = "USAGE_LIMIT_REACHED";

/** The four public reasons, shared by the refusal body, the turn's `error_code` and the status. */
export const USAGE_REASONS = ["person", "team", "organization", "personal_scope"] as const;

export type UsageRefusalReason = (typeof USAGE_REASONS)[number];

export function isUsageReason(value: string | null | undefined): value is UsageRefusalReason {
  return (USAGE_REASONS as readonly (string | null | undefined)[]).includes(value);
}

const LIMIT_SENTENCE = {
  person: "usage.refusal.person",
  team: "usage.refusal.team",
  organization: "usage.refusal.organization",
} as const;

/** Only the date part, written by the server in the zone of the month window. */
const ISO_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})T/;

const DATE_TEXT = new Intl.DateTimeFormat(PLATFORM_LOCALE, {
  day: "numeric",
  month: "long",
  year: "numeric",
  timeZone: "UTC",
});

/** `1 de noviembre de 2026`, or `null` when `iso` carries no readable date. */
export function renewalDateText(iso: string | null | undefined): string | null {
  const parts = ISO_DATE_RE.exec(iso ?? "");
  if (parts === null) return null;
  const [year, month, day] = [Number(parts[1]), Number(parts[2]), Number(parts[3])];
  const date = new Date(Date.UTC(year, month - 1, day));
  // `2026-02-31` would roll over to March: a date that does not exist is not shown.
  if (date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) return null;
  return DATE_TEXT.format(date);
}

/** The sentence for a usage refusal of `reason`, renewing at `renewsAt`. */
export function usageRefusalText(reason: string | null | undefined, renewsAt: string | null | undefined): string {
  if (reason === "personal_scope") return t("usage.refusal.personalScope");
  const sentences = [
    reason === "person" || reason === "team" || reason === "organization"
      ? t(LIMIT_SENTENCE[reason])
      : t("usage.refusal.other"),
  ];
  const date = renewalDateText(renewsAt);
  if (date !== null) sentences.push(t("usage.refusal.renews", { date }));
  sentences.push(t("usage.refusal.contact"));
  return sentences.join(" ");
}
